/**
 * ConsentBanner.tsx — the cookie-consent modal that drives Consent Mode v2.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: tracking A1 (consent).
 *
 * WHY: analytics/ads storage is non-essential on a UK health app, so it is
 * OPT-IN. `consent.ts` defaults every non-essential type to DENIED before GTM
 * loads; this is the UI that lets the visitor grant (Accept all) or confirm the
 * deny (Reject all), persisting the choice so it does not reshow.
 *
 * SHOWN ONLY where tracking can actually happen: the acquisition journey
 * (`isTrackedPath`) AND only when GTM is configured (`gtmContainerId`) AND the
 * visitor has not chosen yet. The signed-in app (`/patients`, `/clinicians`,
 * `/admin`) never loads GTM, so it never shows this. Consent Mode stays
 * deny-first either way — this surface changes the PROMPT, never the default.
 *
 * ── WHY IT BLOCKS (2026-09-04, CTO) ────────────────────────────────────────
 * It used to be a dismissible bottom ribbon, and the ribbon covered the page's
 * primary CTAs: on a phone or a laptop the patient could not find the button
 * they came for, and could not get the ribbon out of the way to look for it. A
 * small bottom-left card was considered and rejected — a white corner card gets
 * ignored, some people deliberately never answer it, and the resulting ambiguity
 * is the actual defect. So the ribbon stays where it is and gains a blocking
 * scrim behind it: deal with this first, then the page. Accept or reject, then
 * straight into the journey.
 *
 * ── THE SCRIM SITS *BELOW* THE R31 CRISIS RIBBON ───────────────────────────
 * `ui/chrome.tsx` pins "Not for emergencies. Call 999 …" to the top of every
 * patient-facing page. A naive full-screen scrim would dim it and eat clicks on
 * it. Nothing about analytics consent may ever sit between a person in crisis
 * and emergency signposting, so the scrim is pinned at `LAYER_CONSENT_SCRIM`,
 * strictly below `LAYER_STICKY_TOP` (see ui/layers.ts). ConsentBanner.spec.tsx
 * fails if that ever inverts.
 *
 * ── WHY THE TWO CHOICES LOOK IDENTICAL ─────────────────────────────────────
 * Reject was `variant="ghost"` (outlined) against a solid Accept. On a
 * dismissible banner that is a mild smell; on a modal that FORCES a choice it is
 * the exact thing the ICO acts on — compelling an answer while making one answer
 * the easier press. Both buttons are now the same variant, the same size and the
 * same flex basis. Do not "improve conversion" by re-emphasising Accept: the
 * blocking behaviour above is only defensible while the two are equal.
 *
 * COPY / policy link are [PLACEHOLDER — legal to confirm]; the mechanism is what
 * ships here. The policy link points at the marketing cookie policy (the same
 * URL the footer uses).
 *
 * ── WHY NOT `ui/ConfirmDialog`'s DialogShell ───────────────────────────────
 * DialogShell was read first and does not fit: it is a CENTRED panel, Escape
 * cancels it, a scrim click cancels it, its scrim is above everything at z-1000,
 * and it documents "no full focus trap" as a deliberate trade-off. Every one of
 * those is wrong here — this is bottom-anchored, has no cancel at all, must not
 * cover the 999 ribbon, and a modal with no exit needs a real trap or a keyboard
 * user tabs into a page they cannot act on. What it DOES lend is the grammar,
 * copied rather than reinvented: `useId` + `aria-labelledby`, the opener captured
 * during the first render (by effect time an autofocused child has already moved
 * focus, so `document.activeElement` there would name a removed node), focus into
 * the panel on mount and back to the opener on unmount, and the latest callback
 * read through a ref so a mount-only effect never re-runs.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { gtmContainerId } from '../config';
import { Button } from '../ui/kit';
import { LAYER_CONSENT_RIBBON, LAYER_CONSENT_SCRIM } from '../ui/layers';
import { usePrefersReducedMotion, useRevealAfterMount } from '../ui/motion';
import { acceptAllConsent, readStoredConsent, rejectAllConsent } from './consent';
import { isTrackedPath } from './gtm';

/** Marketing-hosted cookie policy (mirrors the footer's Cookies link). */
const COOKIE_POLICY_URL = 'https://booking.thegpservice.co.uk/cookie-policy';

/**
 * The veil. DIM, NOT OBSCURE — the visitor is being asked to consent to what
 * happens on this page, so they have to be able to read it. At 35% of the brand
 * ink over white, body text (`--gps-text` #13231b) still resolves to ~7.5:1
 * against the veiled page, comfortably past the 4.5:1 AA floor; the 45% used by
 * the centred ConfirmDialog is tuned to hide its background, which is the
 * opposite requirement.
 */
const SCRIM_INK = 'rgba(19, 35, 27, 0.35)';

/**
 * Anything inside the panel a Tab can land on, in DOM order. The panel itself is
 * `tabIndex=-1` and so is excluded. Deliberately wider than today's content (a
 * link and two buttons): a trap that only knows about the controls that happened
 * to be there when it was written silently stops trapping the day someone adds a
 * "manage categories" field, and there is no Escape to fall back on.
 */
function focusablesIn(panel: HTMLElement): HTMLElement[] {
  return Array.from(
    panel.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  );
}

/**
 * Equal-weight action chrome, shared by BOTH choices so "same size, same
 * prominence" is one object rather than two that can drift apart in a later
 * edit. `flex: 1 1 150px` gives them an identical basis, so they are the same
 * width whether they sit side by side or wrap onto their own rows at 320px;
 * `minHeight: 44px` is the WCAG 2.5.5 target floor.
 */
const CHOICE_STYLE = { width: 'auto', flex: '1 1 150px', maxWidth: '220px', minHeight: '44px', padding: '0 18px' } as const;

export function ConsentBanner() {
  const { pathname } = useLocation();
  // Decided once, this stays hidden for the session even before a reload.
  const [decided, setDecided] = useState(() => readStoredConsent() !== null);

  // Only where GTM actually loads: the acquisition journey, GTM configured, and
  // no prior choice. Fail-closed with the loader — no container id, no prompt.
  // A returning visitor with a recorded choice must never be asked again, and
  // ESPECIALLY not by something that blocks the page.
  if (decided || !gtmContainerId() || !isTrackedPath(pathname)) return null;

  return <ConsentOverlay onDecided={() => setDecided(true)} />;
}

/**
 * The modal proper. Split out from the gate above so its effects (scroll lock,
 * focus trap, focus return) mount and unmount with the modal itself — hooks
 * cannot live behind the gate's early return, and tying the body-scroll lock to
 * the modal's own lifetime is what guarantees the page scrolls again the instant
 * a choice is made.
 */
function ConsentOverlay({ onDecided }: { onDecided: () => void }) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const bodyId = useId();
  const reduced = usePrefersReducedMotion();
  const revealed = useRevealAfterMount(reduced);
  // Captured DURING the first render, not in the effect — see the docblock.
  const openerRef = useRef<HTMLElement | null>(null);
  if (openerRef.current === null) openerRef.current = document.activeElement as HTMLElement | null;

  useEffect(() => {
    const panel = panelRef.current;
    panel?.focus();

    // The page behind is inert: the scrim swallows pointer events and this
    // swallows the keyboard. Two keys matter.
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        // ESCAPE DOES NOT DISMISS. There is no "close" — Accept and Reject are
        // the only exits, which is the entire point of blocking. It is caught
        // and stopped rather than merely ignored so that a page-level Escape
        // handler underneath (a drawer, a lightbox) cannot act on a page the
        // visitor cannot see they are driving.
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (event.key !== 'Tab' || !panel) return;
      const items = focusablesIn(panel);
      if (items.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      const inside = active !== null && panel.contains(active);
      if (event.shiftKey ? active === first || !inside : active === last || !inside) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    }
    // Capture phase: the trap has to win before any page handler sees the key.
    document.addEventListener('keydown', handleKeyDown, true);

    // Lock the page behind, and restore EXACTLY what was there — an empty string
    // is not the same as "no inline overflow", and stamping `auto` on the way out
    // would quietly override a stylesheet that had said something else.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const opener = openerRef.current;
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.body.style.overflow = previousOverflow;
      opener?.focus?.();
    };
  }, []);

  return (
    <>
      <div
        aria-hidden="true"
        // Pointer events are deliberately LEFT ON: swallowing the click is what
        // makes the page behind inert. `preventDefault` on mousedown stops that
        // click from pulling focus out of the modal and onto the body.
        onMouseDown={(event) => event.preventDefault()}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: LAYER_CONSENT_SCRIM, // MUST stay below the R31 ribbon — ui/layers.ts
          background: SCRIM_INK,
          opacity: revealed ? 1 : 0,
          // One reduced-motion signal for the whole estate (ui/motion.tsx); this
          // surface does not get to ask `matchMedia` a second time.
          transition: reduced ? undefined : `opacity var(--gps-dur) var(--gps-ease)`,
        }}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex={-1}
        style={{
          position: 'fixed',
          insetInline: 0,
          bottom: 0,
          zIndex: LAYER_CONSENT_RIBBON,
          background: 'var(--gps-surface)',
          borderTop: '1px solid var(--gps-border)',
          boxShadow: '0 -2px 12px rgba(19, 35, 27, 0.08)',
          padding: '14px 16px',
          outline: 'none',
        }}
      >
        <div
          style={{
            maxWidth: '1100px',
            margin: '0 auto',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--gps-s-3)',
          }}
        >
          {/* THE COPY IS THE ONLY THING THAT SCROLLS. There is no way out of this
              modal except the two buttons, so there must be no way to put them
              off-screen: capping the text at half the viewport (and letting it
              scroll inside that cap) keeps the actions row visible at 320px wide
              and at 200% zoom, where an uncapped ribbon would grow tall enough to
              push its own only exits below the fold. */}
          <div style={{ flex: '1 1 320px', maxWidth: '58ch', maxHeight: '50vh', overflowY: 'auto' }}>
            <h2 id={titleId} style={{ margin: '0 0 4px', fontSize: '15px', color: 'var(--gps-text)' }}>
              Your cookie choices
            </h2>
            <p id={bodyId} style={{ margin: 0, fontSize: '14px', color: 'var(--gps-text)', lineHeight: 1.45 }}>
              {/* [PLACEHOLDER — legal to confirm] */}
              We use analytics and advertising cookies to understand how the service is used and to
              measure our campaigns. You can accept or reject these. Essential cookies are always on.{' '}
              <a href={COOKIE_POLICY_URL} target="_blank" rel="noreferrer" style={{ color: 'var(--gps-link)' }}>
                Cookie policy
              </a>
            </p>
            {/* `aria-modal` tells assistive tech to treat everything outside this
                dialog as inert — including the R31 ribbon overhead, which a
                sighted visitor can still read but a screen-reader user now
                cannot reach. The ribbon carries no controls, so nothing else is
                lost to the trap; the signposting is, so it is restated here in
                one short line rather than left to a cookie prompt to hide.
                Full-strength `--gps-text` (15.4:1 on this surface), not the
                muted grey the rest of this file's small print would use — this
                is the one line here that is not small print. */}
            <p style={{ margin: '6px 0 0', fontSize: '13px', fontWeight: 500, color: 'var(--gps-text)' }}>
              In an emergency, call 999.
            </p>
          </div>
          {/* Reject is listed FIRST and both choices carry identical chrome — see
              the dark-pattern note in the file docblock before restyling either. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--gps-s-2)', justifyContent: 'flex-end' }}>
            <Button
              type="button"
              variant="primary"
              style={CHOICE_STYLE}
              onClick={() => {
                rejectAllConsent();
                onDecided();
              }}
            >
              Reject all
            </Button>
            <Button
              type="button"
              variant="primary"
              style={CHOICE_STYLE}
              onClick={() => {
                acceptAllConsent();
                onDecided();
              }}
            >
              Accept all
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
